import type { ComponentProps, PropsWithChildren } from "react"

import { Tooltip } from "./Tooltip"

/** `Tooltip` placement props, passed straight through. */
export type TooltipPlacement = Pick<
  ComponentProps<typeof Tooltip>,
  "side" | "align" | "disableHoverableContent" | "className"
>

const WRAPPER = "inline-flex"

type Props = TooltipPlacement & {
  /** The explanation. `undefined` shows no bubble but keeps the wrapper in place. */
  title?: string
  /** Extends the wrapper `span`, for children a parent stretches. */
  wrapperClassName?: string
}

/**
 * The tooltip explaining why a control is unavailable. The wrapper `span` is load-bearing: a
 * disabled element fires no pointer events, so Radix needs an enabled element to open from —
 * which is why this must wrap the trigger, never sit inside an `asChild` slot or a
 * `trigger`-style prop (the gated `*Trigger` wrappers exist so the gate can sit outside
 * the slot).
 *
 * The wrapper renders with or without a `title`, so the child never remounts — losing focus and
 * any uncontrolled value — as the explanation comes and goes.
 */
export function DisabledTooltip({
  title,
  wrapperClassName,
  children,
  ...placement
}: PropsWithChildren<Props>) {
  return (
    <Tooltip title={title} {...placement}>
      <span className={wrapperClassName === undefined ? WRAPPER : `${WRAPPER} ${wrapperClassName}`}>
        {children}
      </span>
    </Tooltip>
  )
}
